import { Box } from '@mui/material';
import { neutral } from '@ahora-patagonia/design-tokens';
import type { AdPlacement, PublicAd } from '@ahora-patagonia/contracts';
import { ALTURAS, ANCHOS } from './alturas.js';
import { useSenalesDeAviso } from '../senales/useSenalesDeAviso.js';

export interface EspacioPublicitarioProps {
  ubicacion: AdPlacement;
  /** El aviso resuelto por el servidor, o `undefined` si la ubicación no tiene campaña. */
  ad: PublicAd | undefined;
}

/** Alto del rótulo "Publicidad": entra en el alto total reservado. */
const ALTO_ROTULO = 18;

/**
 * Un espacio publicitario del sitio (handoff §8, las siete reglas):
 *
 *  - **Sin campaña, no se renderiza nada**: ni rectángulo gris, ni cartel, ni alto en vano.
 *  - El **alto se reserva por corte** en el HTML del server: ningún aviso mueve el texto.
 *  - El rótulo "Publicidad" va adentro, arriba a la izquierda, gris (es del sistema, no
 *    del diario): nadie confunde un aviso con una noticia.
 *  - Un snippet HTML ajeno va en un **iframe** con el alto justo: no puede tocar el DOM de
 *    la página, ni empujarla, ni romper la hidratación (el `srcDoc` es un string estable).
 *
 * Y mide: impresión visible y clic (`useSenalesDeAviso`). Todos los avisos del sitio pasan por acá.
 */
export function EspacioPublicitario({ ubicacion, ad }: EspacioPublicitarioProps) {
  const senales = useSenalesDeAviso(ad?.id);
  if (!ad) return null;

  const alto = ALTURAS[ubicacion];
  const ancho = ANCHOS[ubicacion];

  return (
    <Box
      component="aside"
      ref={senales.ref}
      aria-label="Publicidad"
      sx={{
        border: `1px solid ${neutral[300]}`,
        mx: 'auto',
        width: '100%',
        maxWidth: { xs: ancho.base + 2, sm: ancho.desde600 + 2, lg: ancho.desde1200 + 2 },
      }}
    >
      <Box
        sx={{
          fontSize: 9,
          lineHeight: `${ALTO_ROTULO}px`,
          fontWeight: 400,
          textTransform: 'uppercase',
          letterSpacing: '0.16em',
          color: neutral[600],
          px: 0.5,
        }}
      >
        Publicidad
      </Box>
      <Box
        sx={{
          height: { xs: alto.base, sm: alto.desde600, lg: alto.desde1200 },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {ad.type === 'IMAGE' && ad.image?.url ? (
          <a
            href={ad.targetUrl ?? '#'}
            target="_blank"
            rel="nofollow sponsored noopener"
            onClick={senales.alClic}
            style={{ display: 'block', width: '100%', height: '100%' }}
          >
            <img
              src={ad.image.url}
              alt={ad.image.alt ?? 'Aviso publicitario'}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </a>
        ) : ad.htmlSnippet ? (
          <iframe
            ref={senales.iframeRef}
            title="Publicidad"
            srcDoc={`<!doctype html><html><body style="margin:0;display:flex;align-items:center;justify-content:center;">${ad.htmlSnippet}</body></html>`}
            style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            loading="lazy"
          />
        ) : null}
      </Box>
    </Box>
  );
}
